import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import PersonCell from '../../../components/ui/PersonCell';

export const DOC_TYPES = [
  'ibraname', 'nda_renewal', 'non_compete', 'severance_agreement', 'sgk_exit_form',
  'military_leave_record', 'marriage_record', 'health_report', 'disability_report', 'other',
];
export const STATUSES = ['pending', 'prepared', 'sent', 'signed', 'stored', 'cancelled'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useOdLegalDocsApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboarding-legal-docs?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const prepare = useCallback(async (id, fileUrl) => {
    try { await api.post(`/v1/offboarding-legal-docs/${id}/prepare`, { fileUrl }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const send = useCallback(async (id, provider, requestId) => {
    try { await api.post(`/v1/offboarding-legal-docs/${id}/send`, { eSignatureProvider: provider, eSignatureRequestId: requestId }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const markSigned = useCallback(async (id) => {
    try { await api.post(`/v1/offboarding-legal-docs/${id}/mark-signed`, {}); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const archive = useCallback(async (id) => {
    try { await api.post(`/v1/offboarding-legal-docs/${id}/archive`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    try { await api.post(`/v1/offboarding-legal-docs/${id}/cancel`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const docTypeLabel = (s) => t(`offboardingLegalDoc.type.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`offboardingLegalDoc.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'offboardingId', label: 'OD', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('offboardingLegalDoc.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ),
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like', filterKey: 'personName[like]' },
        },
      },
      {
        name: 'title',
        label: t('offboardingLegalDoc.title'),
        options: {
          filter: true, sort: true,
          filterType: 'textField',
          filterOptions: { defaultOp: 'like' },
        },
      },
      {
        name: 'docType',
        label: t('offboardingLegalDoc.typeLabel'),
        options: {
          ...multiSelectFilter(t('offboardingLegalDoc.typeLabel'), DOC_TYPES, docTypeLabel),
          customBodyRender: (v) => docTypeLabel(v),
        },
      },
      {
        name: 'status',
        label: t('offboardingLegalDoc.statusLabel'),
        options: {
          ...multiSelectFilter(t('offboardingLegalDoc.statusLabel'), STATUSES, statusLabel),
          customBodyRender: (v) => statusLabel(v),
        },
      },
      {
        name: 'isMandatory',
        label: t('offboardingLegalDoc.isMandatory'),
        options: { filter: true, sort: true, customBodyRender: (v) => v ? t('common.yes') : t('common.no') },
      },
      {
        name: 'kvkkSpecialCategory',
        label: t('offboardingLegalDoc.kvkkSpecialCategory'),
        options: { filter: true, sort: true, customBodyRender: (v) => v ? t('common.yes') : t('common.no') },
      },
    ];
  }, [t, data]);

  return { fetching, data, pagination, columns, refresh, prepare, send, markSigned, archive, cancel };
};

export default useOdLegalDocsApi;
